import { type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Switch,
  Text,
  type OptionOnSelectData,
  type SelectionEvents,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  switchWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  labelSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  // Group the Switch and any caller-provided end actions so they sit
  // together at the right side of the row. The Switch itself isn't a
  // bordered input box, so chips live next to it rather than inside it.
  controlGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  dropdown: {
    minWidth: '120px',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
});

export interface BooleanFieldProps {
  name: string;
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  description?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /**
   * Render style. `'switch'` (default) shows a Fluent Switch. `'dropdown'`
   * shows an explicit True / False Dropdown — useful when the parameter
   * carries semantic meaning beyond on/off (e.g. a connector option that
   * defaults to undefined and needs an explicit user choice).
   */
  mode?: 'switch' | 'dropdown' | undefined;
  /**
   * Optional slot rendered next to the control on the right side of the
   * row. Used to host inline affordances like `FieldActionChips` for
   * tokenizable boolean parameters.
   */
  endActions?: ReactNode | undefined;
}

/**
 * BooleanField - True/false input as a Switch (default) or Dropdown.
 */
export const BooleanField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled,
  error,
  mode = 'switch',
  endActions,
}: BooleanFieldProps) => {
  const styles = useStyles();

  const handleDropdownChange = (_: SelectionEvents, data: OptionOnSelectData) => {
    if (data.optionValue === 'true') onChange(true);
    else if (data.optionValue === 'false') onChange(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.switchWrapper}>
        <div className={styles.labelSection}>
          <Text className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </Text>
          {description && !error && (
            <Text className={styles.description}>
              {description}
            </Text>
          )}
        </div>
        <div className={styles.controlGroup}>
          {mode === 'dropdown' ? (
            <Dropdown
              id={name}
              className={styles.dropdown}
              value={value ? 'True' : 'False'}
              selectedOptions={[String(value)]}
              onOptionSelect={handleDropdownChange}
              disabled={disabled}
              aria-describedby={description ? `${name}-desc` : undefined}
            >
              <Option value="true">True</Option>
              <Option value="false">False</Option>
            </Dropdown>
          ) : (
            <Switch
              id={name}
              checked={value}
              onChange={(_, data) => onChange(data.checked)}
              disabled={disabled}
              aria-describedby={description ? `${name}-desc` : undefined}
            />
          )}
          {endActions}
        </div>
      </div>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

BooleanField.displayName = 'BooleanField';
